import { Splide, SplideProps } from '@splidejs/react-splide'
import { Options as SplideOptions, Splide as SplideType } from '@splidejs/splide'
import { useDrag } from '@use-gesture/react'
import debounce from 'lodash/debounce'
import includes from 'lodash/includes'
import merge from 'lodash/merge'
import noop from 'lodash/noop'
import React, { forwardRef, Ref, useCallback, useEffect, useRef, useState } from 'react'
import { useKeyPressEvent } from 'react-use'
import styled, { css } from 'styled-components'

import SVGClose from '@Assets/SVG/Close'
import { PersonalizationCaption } from '@Components/Personalization/Styles/Personalization.styled'
import { Product } from '@Generated/graphql'
import { up } from '@Globals/Style/breakpoints'
import { blackImageOverlay, buttonReset } from '@Globals/Style/mixins'
import theme from '@Globals/Style/theme'
import { useTranslation } from '@Globals/Translations'

import { DataSlider } from './components/DataSlider'
import { PaginateVideoExtension, paginateVideoExtensionCSS } from './extensions/PaginateVideoExtension'
import { Slider } from './Slider'
import useShareForwardedRef from './utils/useShareForwardedRef'

const breakpointName = 'mobileStyleThreshold'
const breakpoint = parseInt(theme.breakpoints[breakpointName], 10) - 1
const SWIPE_UP_ANIMATION_DURATION = 500
export const CloseButton = styled.button`
  ${buttonReset};
  background: rgba(245, 245, 245, .6);
  padding: 10px 14px;
  margin-left: 12px;
  margin-top: 4px;

  > svg {
    width: 14px;
    height: 14px;
  }

  > svg:hover {
    transform: scale(1.2);
    transition-duration: .05s;
  }
`

const SplideSlider = styled(Slider)(({ isFullscreen }) => css`
  overflow: hidden;
  width: 100%;

  .splide__arrow {
    display: none;
    position: ${(isFullscreen ? 'fixed' : '')};

    ${up(breakpointName)} {
      display: flex;
    }
  }

  .splide__arrow--prev {
    left: 0;
  }

  .splide__arrow--next {
    right: 0;
  }

  .splide__track {
    height: 100%;
    width: 100%;

    .splide__slide {
      height: 100%;
      display: flex;
      margin-top: ${(isFullscreen ? '48px' : '0')};

      ${up('phablet')} {
        margin-top: 0;
      }

      align-items: center;
      outline: 0 !important;
      justify-content: center;
      outline-offset: 0 !important;

      &:focus {
        outline: 0 !important;
        outline-offset: 0 !important;
      }
    }
  }

  li button {
    background: white;
  }

  .splide__pagination {
    position: absolute;
    display: flex;
    justify-content: center;
    bottom: 0;
    height: 50px;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    padding: 0;
    transition: transform .3s ease;

    > li {
      transition: opacity .45s ease;
    }

    .splide__pagination__page {
      border: solid 1px rgba(124, 124, 124, 0.8);
      background-color: white;
      opacity: .7;

      &.is-active {
        border-color: rgba(255, 255, 255, 0.75);
        background-color: rgb(56, 56, 56);
        opacity: 1;
      }
    }

    .video-icon {
      position: relative;
      border-radius: 50%;
      border: 1px solid ${theme.colors.greyLight};
      width: 16px;
      height: 16px;
      max-width: 16px;
      max-height: 16px;

      button {
        position: absolute;
        top: 0;
        left: 0;
        border: 0;
        width: 14px;
        height: 14px;
        margin: 0;
        max-width: 14px;
        max-height: 14px;
      }
    }

    .video-icon::after {
      position: absolute;
      display: block;
      content: ' ';
      width: 0;
      height: 0;
      z-index: 10;
      left: 5px;
      top: 3px;
      border-width: 4px 0 4px 5px;
      border-color: transparent transparent transparent ${theme.colors.greyLight};
      border-style: solid;
      pointer-events: none;
    }
  }

  ${paginateVideoExtensionCSS}
`)

const HorizontalSliderRoot = styled.div<{ disabledFullscreen: boolean, isFullscreen: boolean, overlayActive: boolean, swipe: boolean }>(({
  disabledFullscreen,
  isFullscreen,
  overlayActive,
  swipe,
}) => css`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 100%;
  min-height: ${disabledFullscreen ? '0' : '370px'};
  max-width: 100%;
  padding-bottom: 50px;
  max-height: 585px;

  ${up('mobileStyleThreshold')} {
    width: auto;
    height: auto;
    max-width: 600px;
    max-height: 600px;
    aspect-ratio: 1;
    padding-bottom: 0;
  }

  ${overlayActive && css`
    ${blackImageOverlay}
  `}
  ${up('mobileStyleThreshold')} {
    .splide > .splide__pagination {
      display: none;
    }
  }

  touch-action: ${isFullscreen ? 'none' : 'pan-y'};

  ${isFullscreen && css`
    background: ${theme.colors.white};
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    min-width: 100%;
    z-index: 1000;
    max-height: 100%;

    ${up('mobileStyleThreshold')} {
      width: 100%;
      height: 100%;
      max-height: 100%;
      max-width: 100%;

      .splide > .splide__pagination {
        display: flex;
      }
    }

    ${swipe && css`
      animation: slide-up-opacity cubic-bezier(0.4, 0, 0.2, 1) forwards;
      animation-duration: ${SWIPE_UP_ANIMATION_DURATION}ms;

      @keyframes slide-up-opacity {
        0% {
          transform: translateY(0%);
          opacity: 0;
        }
        100% {
          transform: translateY(-100%);
          opacity: 1;
        }
      }
    `}
  `}
  ${SplideSlider} {
    ${disabledFullscreen && css`
      min-height: 280px;
    `};

    .splide__arrow--prev {
      ${disabledFullscreen && css`
        top: 140px;
      `};
    }

    .splide__arrow--next {
      ${disabledFullscreen && css`
        top: 140px;
      `};
    }
  }
`)

const FullscreenControls = styled.div`
  display: flex;
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 1001;
`

const defaultProps: SplideOptions = {
  extensions: {
    PaginateVideoExtension,
  },
  options: {
    perPage: 1,
    breakpoints: {
      [breakpoint]: {
        arrows: false,
        pagination: true,
      },
      9999: {
        arrows: true,
        pagination: true,
      },
    },
  },
}

interface HorizontalSliderProp extends SplideProps, Pick<Product, 'assets'> {
  captions?: string[] | null
  disableFullscreen?: boolean
  isFullscreen?: boolean
  onClose?: () => void
  overlayActive?: boolean
  ref?: Ref<Splide>
  title?: string
}

const centerPagination = (data, paginationElement, disableFullscreen) => {
  if (data.items.length > 5 && paginationElement) {
    // eslint-disable-next-line no-param-reassign
    paginationElement.style.transform = disableFullscreen
      ? 'translateX(calc(-50%)'
      : `translateX(calc(-50% + ${data.items.length < 7 ? 7 : Math.ceil(data.items.length * 15) / 2 - 37.5}px )`
  }
}

const ImageCaption = styled(PersonalizationCaption)`
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  margin-bottom: 0;
  padding: 15px 15% 10px;
  font-weight: 400;
`

export const HorizontalSlider: React.FC<HorizontalSliderProp> = forwardRef(
  ({
    assets,
    captions = null,
    className,
    disableFullscreen = false,
    isFullscreen = false,
    onClose,
    onMove,
    overlayActive = true,
    title,
    ...componentProps
  }, ref: Ref<Splide>) => {
    const horizontalRef = useRef<HTMLDivElement & { swipe: boolean }>(null)
    const forwardedRef = useShareForwardedRef(ref)
    const { t } = useTranslation()
    const [splide, setSplide] = useState<SplideType | undefined>()
    const [captionIndex, setCaptionIndex] = useState(0)

    const closeFullscreen = useCallback(() => (isFullscreen && onClose) && onClose(), [isFullscreen, onClose])
    useKeyPressEvent('ArrowLeft', null, () => isFullscreen && splide?.go('-1'))
    useKeyPressEvent('ArrowRight', null, () => isFullscreen && splide?.go('+1'))
    useEffect(() => {
      if (typeof window === 'undefined') {
        return () => {
        }
      }

      window.addEventListener('resize', () => closeFullscreen())

      return () => window.removeEventListener('resize', () => closeFullscreen())
    }, [closeFullscreen])

    useKeyPressEvent('Escape', null, () => closeFullscreen())

    useEffect(() => {
      if (forwardedRef.current && captions) {
        forwardedRef.current.splide?.on('move', slideIndex => setCaptionIndex(slideIndex))
      }
    }, [captions, forwardedRef])

    useEffect(() => {
      if (forwardedRef?.current?.splide) {
        setSplide(forwardedRef.current.splide)
      }
    }, [forwardedRef])

    const paginationSetup = useCallback(() => {
      if (forwardedRef && 'current' in forwardedRef) {
        const paginationData = forwardedRef.current?.splide?.Components.Pagination
        if (paginationData) {
          const paginationElement = document.getElementsByClassName('splide__pagination')[0] as HTMLElement

          centerPagination(paginationData, paginationElement, disableFullscreen)
          paginationData.items.forEach((item) => {
            if (item.page > 4 && !disableFullscreen) {
              // eslint-disable-next-line no-param-reassign
              item.li.style.opacity = '0'
            }
            const containsVideo = assets ? includes(assets[item.page].tags, 'video') : false
            if (containsVideo) {
              item.li.classList.add('video-icon')
            }
          })
        }

        const paginationElement = document.getElementsByClassName('splide__pagination')[0] as HTMLElement
        forwardedRef.current?.splide?.on('pagination:updated', (data) => {
          const currentIndex = forwardedRef?.current?.splide?.index
          centerPagination(data, paginationElement, disableFullscreen)
          const numberOfElements = data.items.length

          if (currentIndex && currentIndex > 2 && paginationElement) {
            const percievedElements = currentIndex > numberOfElements - 3 ? numberOfElements - 2 : numberOfElements
            const percievedIndex = currentIndex > numberOfElements - 3 ? numberOfElements - 4 : currentIndex

            // Position slider in center on update
            paginationElement.style.transform = disableFullscreen
              ? 'translateX(calc(-50%)'
              : `translateX(calc(-50% + ${numberOfElements < 7 ? 7 : Math.ceil(percievedElements * 15) / 2 - 37.5 - (percievedIndex - 2) * 15}px)`
          }

          data.items.forEach((item, index) => {
            // Reason as to it cannot be !currentIndex is because the value 0 is a falsy valye
            // and will break the spin.
            if (typeof currentIndex === 'undefined' || disableFullscreen) {
              return
            }

            // If dot is either belowFold of center or above fold.
            if (index < currentIndex - 2 || index > currentIndex + 2) {
              // edge cases
              const belowFold = currentIndex < 3 && index < 5
              const aboveFold = currentIndex > numberOfElements - 4 && index > numberOfElements - 6
              // eslint-disable-next-line no-param-reassign
              item.li.style.opacity = belowFold || aboveFold ? '1' : '0'
            } else {
              // eslint-disable-next-line no-param-reassign
              item.li.style.opacity = '1'
            }
          })
        })
      }
    }, [assets, disableFullscreen, forwardedRef])

    useEffect(() => {
      if (forwardedRef.current && captions) {
        forwardedRef.current.splide?.on('move', slideIndex => setCaptionIndex(slideIndex))
      }
    }, [captions, forwardedRef])

    useEffect(() => {
      if (forwardedRef?.current?.splide) {
        setSplide(forwardedRef.current.splide)
        paginationSetup()
      }
    }, [forwardedRef, paginationSetup])

    const handleResize = useCallback(() => {
      paginationSetup()
      closeFullscreen()
    }, [closeFullscreen, paginationSetup])

    useEffect(() => {
      if (typeof window === 'undefined') {
        return noop
      }

      const debouncedResize = debounce(handleResize, 50)
      window.addEventListener('resize', debouncedResize)

      return () => {
        window.removeEventListener('resize', debouncedResize)
      }
    }, [handleResize])

    useKeyPressEvent('Escape', null, () => closeFullscreen())
    const [swipe, setSwipe] = useState(false)

    useDrag(({ swipe: [, my] }) => {
      if (isFullscreen && my < 0 && horizontalRef.current) {
        setSwipe(true)
        setTimeout(() => {
          if (isFullscreen && horizontalRef.current) {
            setSwipe(false)
            onClose!()
          }
        }, SWIPE_UP_ANIMATION_DURATION)
      }
    }, { target: horizontalRef })

    const props = merge({}, defaultProps, componentProps)
    return (
      <HorizontalSliderRoot
        className={className}
        disabledFullscreen={disableFullscreen}
        isFullscreen={isFullscreen}
        overlayActive={overlayActive}
        ref={horizontalRef}
        swipe={swipe}
      >
        <SplideSlider isFullscreen={isFullscreen} {...props} ref={forwardedRef as any}>
          <DataSlider
            assets={assets}
            disableFullscreen={disableFullscreen}
            splide={splide}
            title={title}
            type={isFullscreen ? 'fullscreen' : undefined}
          />
        </SplideSlider>
        {!disableFullscreen && (
          <FullscreenControls>
            {onClose && isFullscreen && (
              <CloseButton onClick={onClose}>
                <SVGClose />
              </CloseButton>
            )}
          </FullscreenControls>
        )}
        {captions && (
          <ImageCaption center>
            &quot;
            {t(captions[captionIndex])}
            &quot;
          </ImageCaption>
        )}

      </HorizontalSliderRoot>
    )
  },
)
